<template>
  <div class="ai-chat-container">
    <!-- 头部区域 -->
    <div class="chat-header">
      <div class="header-content">
        <div class="logo">
          <img src="@/assets/jh-logo.png" alt="九火互娱" class="logo-image" />
          <div class="logo-text">智能小助手</div>
        </div>
        <div class="header-actions">
          <button class="action-button" @click="clearHistory" title="清空对话">
            <i class="fas fa-trash-alt"></i>
            <span>清空对话</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 对话区域 -->
    <div class="chat-body" ref="chatBody">
      <!-- 欢迎提示区域：仅在对话还未展开时显示 -->
      <div class="welcome-screen" v-if="messages.length === 1">
        <!-- <div class="welcome-header">
          <i class="fas fa-robot"></i>
          <h2>九火智能助手</h2>
        </div> -->
        <div class="suggestion-grid">
          <button
            v-for="(suggestion, index) in suggestions"
            :key="index"
            class="suggestion-btn"
            @click="sendMessage(suggestion.text)"
          >
            <i :class="suggestion.icon"></i>
            <span>{{ suggestion.text }}</span>
          </button>
        </div>
      </div>

      <!-- 消息列表（采用 transition-group 以实现入场动画） -->
      <transition-group
        name="message"
        tag="div"
        class="messages-container"
        ref="messageBox"
      >
        <div
          v-for="(message, index) in messages"
          :key="index"
          :class="['message-wrapper', message.type]"
        >
          <div class="message-avatar">
            <i
              :class="message.type === 'bot' ? 'fas fa-robot' : 'fas fa-user'"
            ></i>
          </div>
          <div class="message-content">
            <div class="message-header">
              <span class="sender">{{
                message.type === "bot" ? "智能助手" : "我"
              }}</span>
              <span class="time">{{ message.time }}</span>
            </div>
            <div
              class="message-text"
              v-html="formatMessage(message.text)"
            ></div>
            <!-- 正在生成回答时，在最后一条 bot 消息旁显示新的加载动画 -->
            <div
              v-if="
                message.type === 'bot' &&
                isLoading &&
                index === messages.length - 1
              "
              class="loading-wrapper"
            >
              <div class="loading-animation">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
            <!-- 仅对最后一条 bot 消息显示操作按钮 -->
            <div
              class="message-actions"
              v-if="message.type === 'bot' && index === messages.length - 1"
            >
              <button
                class="action-btn"
                @click="copyMessage(message.text)"
                title="复制"
              >
                <i class="fas fa-copy"></i>
              </button>
              <button
                v-if="isLoading"
                class="action-btn stop-btn"
                @click="stopAnswer"
                title="停止回答"
              >
                <i class="fas fa-stop"></i>
              </button>
              <button
                v-else
                class="action-btn redo-btn"
                @click="reAnswer"
                title="重新回答"
              >
                <i class="fas fa-redo"></i>
              </button>
              <button
                class="action-btn collapse-btn"
                @click="toggleCollapse(index)"
                title="折叠/展开"
              >
                <i
                  :class="
                    collapsed[index]
                      ? 'fas fa-chevron-down'
                      : 'fas fa-chevron-up'
                  "
                ></i>
              </button>
            </div>
          </div>
        </div>
      </transition-group>
    </div>

    <!-- 输入区域 -->
    <div class="chat-input">
      <div class="input-wrapper">
        <textarea
          v-model="userInput"
          @keydown.enter.prevent="handleEnter"
          placeholder="输入您的问题，按Enter发送，Shift+Enter换行"
          :disabled="isLoading"
          ref="inputArea"
          rows="1"
          @input="autoResize"
        ></textarea>
        <button
          class="send-btn"
          @click="sendMessage()"
          :disabled="isLoading || !userInput.trim()"
        >
          <i
            :class="isLoading ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'"
          ></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from "vue";
import { marked } from "marked";
import hljs from "highlight.js";
import "highlight.js/styles/atom-one-dark.css";

const chatBody = ref(null);
const messageBox = ref(null);
const inputArea = ref(null);
const userInput = ref("");
const isLoading = ref(false);
const apiKey = "sk-3546b1985a6943af9c6075df638737a4";

const suggestions = [
  { text: "介绍一下九火互娱的技术服务", icon: "fas fa-info-circle" },
  { text: "你们的技术团队有什么优势？", icon: "fas fa-users" },
  { text: "介绍一下你们的开发流程？", icon: "fas fa-code-branch" },
  { text: "你们的收费标准是怎样的？", icon: "fas fa-dollar-sign" },
];

const messages = ref([
  {
    type: "bot",
    text: "您好！我是九火互娱的AI助手，可以为您提供技术咨询、项目评估等服务。请问有什么可以帮您？",
    time: getCurrentTime(),
  },
]);

// 折叠状态
const collapsed = ref([]);

// 添加 AbortController 的引用
const controller = ref(null);

// 配置 marked 选项，增加对加粗语法的支持
marked.use({
  gfm: true,
  breaks: true,
  highlight: function (code, lang) {
    if (lang && hljs.getLanguage(lang)) {
      return hljs.highlight(code, { language: lang }).value;
    }
    return hljs.highlightAuto(code).value;
  },
  // 添加对行内格式的支持
  renderer: {
    text(text) {
      // 处理加粗语法
      text = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
      // 处理斜体语法
      text = text.replace(/\*(.*?)\*/g, "<em>$1</em>");
      return text;
    },
  },
});

function getCurrentTime() {
  const now = new Date();
  return `${now.getHours().toString().padStart(2, "0")}:${now
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;
}

// 格式化消息内容
const formatMessage = (text) => {
  if (!text) return "";

  // 检测是否包含特殊格式标记
  if (
    text.includes("```") ||
    text.includes("|------") ||
    text.includes("**") || // 添加对加粗语法的检测
    text.includes("*") || // 添加对斜体语法的检测
    text.includes("#")
  ) {
    // 添加对标题语法的检测
    return marked(text);
  }

  // 检测是否是表格格式
  if (text.includes("|------|")) {
    return marked(text);
  }

  // 检测是否包含图表描述
  if (text.includes("```mermaid")) {
    // 需要额外处理 mermaid 图表
    return processMermaidChart(text);
  }

  // 普通文本直接返回，保持换行
  return text.replace(/\n/g, "<br>");
};

// 处理 mermaid 图表
const processMermaidChart = (text) => {
  // 这里需要集成 mermaid.js 来渲染图表
  // 可以根据需要添加具体实现
  return text;
};

function autoResize() {
  const textarea = inputArea.value;
  textarea.style.height = "auto";
  textarea.style.height = Math.min(textarea.scrollHeight, 150) + "px";
}

function handleEnter(e) {
  if (e.shiftKey) return;
  sendMessage();
}

async function sendMessage(text = null) {
  const message = text || userInput.value.trim();
  if (!message || isLoading.value) return;

  // 添加用户消息
  messages.value.push({
    type: "user",
    text: message,
    time: getCurrentTime(),
  });

  // 清空输入框并重置高度
  userInput.value = "";
  if (inputArea.value) {
    inputArea.value.style.height = "auto";
  }

  // 添加机器人消息占位
  messages.value.push({
    type: "bot",
    text: "",
    time: getCurrentTime(),
  });

  isLoading.value = true;
  // 创建新的 AbortController
  controller.value = new AbortController();

  try {
    const response = await fetch(
      "https://api.deepseek.com/v1/chat/completions",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: "deepseek-chat",
          messages: [
            {
              role: "system",
              content:
                "你是九火互娱的AI助手，可以为用户提供技术咨询、项目评估等服务。请用专业、友好的语气回答用户的问题。",
            },
            {
              role: "user",
              content: message,
            },
          ],
          stream: true,
          temperature: 0.7,
        }),
        // 添加 signal 以支持中断请求
        signal: controller.value.signal,
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const currentMessage = messages.value[messages.value.length - 1];

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (!line.trim() || !line.startsWith("data: ")) continue;

          try {
            const jsonStr = line.replace(/^data: /, "");
            const json = JSON.parse(jsonStr);

            if (json.choices?.[0]?.delta?.content) {
              currentMessage.text += json.choices[0].delta.content;
              await nextTick(() => {
                smoothScrollToBottom();
              });
            }
          } catch (e) {
            console.error("Error parsing chunk:", e);
          }
        }
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Fetch aborted");
        return;
      }
      throw error;
    }
  } catch (error) {
    console.error("Error:", error);
    const currentMessage = messages.value[messages.value.length - 1];
    if (error.name !== "AbortError") {
      currentMessage.text = "抱歉，发生了一些错误，请稍后重试。";
    }
  } finally {
    isLoading.value = false;
    controller.value = null;
    await nextTick(() => {
      smoothScrollToBottom();
    });
  }
}

// 添加平滑滚动函数
function smoothScrollToBottom() {
  const chatBodyEl = chatBody.value;
  if (!chatBodyEl) return;

  const scrollHeight = chatBodyEl.scrollHeight;
  const currentScroll = chatBodyEl.scrollTop;
  const clientHeight = chatBodyEl.clientHeight;
  const bottomThreshold = scrollHeight - clientHeight - 100; // 距离底部100px时就开始滚动

  // 只有当用户已经接近底部时才自动滚动
  if (currentScroll >= bottomThreshold) {
    chatBodyEl.scrollTo({
      top: scrollHeight,
      behavior: "smooth",
    });
  }
}

// 修改 stopAnswer 函数，改用 AbortController 来中断流式请求
function stopAnswer() {
  if (controller.value) {
    controller.value.abort(); // 中断请求
    controller.value = null;
  }
  isLoading.value = false;

  const lastMessage = messages.value[messages.value.length - 1];
  if (lastMessage && lastMessage.type === "bot") {
    lastMessage.text += "\n[回答已终止]";
  }

  // 重置状态
  nextTick(() => {
    smoothScrollToBottom();
  });
}

// 重新回答操作：删除最新的bot消息，并以最新的用户消息重新发起请求
function reAnswer() {
  // 如果正在加载，先停止当前回答
  if (isLoading.value) {
    stopAnswer();
  }
  // 获取倒数第二条消息（用户的问题）
  const userMessage = messages.value[messages.value.length - 2];
  if (userMessage && userMessage.type === "user") {
    // 删除最后一条bot的回答
    messages.value.pop();
    // 重新发送用户的问题
    sendMessage(userMessage.text);
  }
}

// 折叠/展开消息
function toggleCollapse(index) {
  collapsed.value[index] = !collapsed.value[index];
}

async function copyMessage(text) {
  try {
    await navigator.clipboard.writeText(text);
    alert("内容已复制到剪贴板");
  } catch (error) {
    console.error("复制失败", error);
  }
}

function scrollToBottom() {
  if (messageBox.value) {
    nextTick(() => {
      messageBox.value.scrollTop = messageBox.value.scrollHeight;
    });
  }
}

// 修改 clearHistory 函数，添加确认对话框
function clearHistory() {
  if (confirm("确定要清空所有对话记录吗？")) {
    // 保留第一条欢迎消息
    messages.value = [
      {
        type: "bot",
        text: "您好！我是九火互娱的AI助手，可以为您提供技术咨询、项目评估等服务。请问有什么可以帮您？",
        time: getCurrentTime(),
      },
    ];
    // 重置折叠状态
    collapsed.value = [];
    // 清空输入框
    userInput.value = "";
    // 如果正在加载，停止加载
    if (isLoading.value) {
      stopAnswer();
    }
  }
}

onMounted(() => {
  smoothScrollToBottom();
  window.addEventListener("resize", () => {
    nextTick(() => {
      smoothScrollToBottom();
    });
  });

  // 优化滚动事件处理
  const chatBody = document.querySelector(".chat-body");
  if (chatBody) {
    let lastScrollTop = 0;

    chatBody.addEventListener(
      "wheel",
      (e) => {
        const { scrollTop, scrollHeight, clientHeight } = chatBody;
        const isScrollingDown = e.deltaY > 0;
        const isAtTop = scrollTop <= 0;
        const isAtBottom =
          Math.abs(scrollHeight - clientHeight - scrollTop) < 1;

        // 向上滚动时
        if (!isScrollingDown && isAtTop) {
          // 如果已经在顶部，允许页面滚动
          return;
        }

        // 向下滚动时
        if (isScrollingDown && isAtBottom) {
          // 如果已经在底部，允许页面滚动
          return;
        }

        // 在聊天内容中间滚动时，阻止事件冒泡
        e.stopPropagation();

        // 更新滚动位置
        lastScrollTop = scrollTop;
      },
      { passive: true }
    );

    // 添加触摸事件支持
    let touchStartY = 0;

    chatBody.addEventListener(
      "touchstart",
      (e) => {
        touchStartY = e.touches[0].clientY;
      },
      { passive: true }
    );

    chatBody.addEventListener(
      "touchmove",
      (e) => {
        const { scrollTop, scrollHeight, clientHeight } = chatBody;
        const touchY = e.touches[0].clientY;
        const isScrollingDown = touchStartY > touchY;
        const isAtTop = scrollTop <= 0;
        const isAtBottom =
          Math.abs(scrollHeight - clientHeight - scrollTop) < 1;

        if ((!isScrollingDown && isAtTop) || (isScrollingDown && isAtBottom)) {
          return;
        }

        e.stopPropagation();
      },
      { passive: true }
    );
  }
});
</script>

<style scoped>
/* 修复横向滚动条 */
.chat-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden; /* 添加这行来隐藏横向滚动条 */
  padding: 30px;
  position: relative;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  height: 100%;
  max-height: calc(100vh - 200px);
  background: linear-gradient(135deg, #f8fafc 0%, #fff9ef 100%);
}

/* 优化动态背景效果 */
/* .chat-body::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  right: -50%;
  bottom: -50%;
  background: radial-gradient(
      circle at 30% 20%,
      rgba(245, 187, 68, 0.05) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 70% 80%,
      rgba(245, 187, 68, 0.05) 0%,
      transparent 50%
    );
  animation: rotateBackground 20s linear infinite;
  pointer-events: none;
  z-index: 0;
} */

/* 优化网格纹理动画 */
.chat-body::after {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  right: -50%;
  bottom: -50%;
  background-image: linear-gradient(
      rgba(245, 187, 68, 0.03) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(245, 187, 68, 0.03) 1px, transparent 1px);
  background-size: 30px 30px;
  animation: movePattern 15s linear infinite;
  pointer-events: none;
  z-index: 0;
}

/* 添加新的背景动画 */
@keyframes rotateBackground {
  0% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(180deg) scale(1.1);
  }
  100% {
    transform: rotate(360deg) scale(1);
  }
}

@keyframes movePattern {
  0% {
    transform: translate(0, 0) rotate(0deg);
  }
  25% {
    transform: translate(10px, 10px) rotate(1deg);
  }
  50% {
    transform: translate(0, 20px) rotate(0deg);
  }
  75% {
    transform: translate(-10px, 10px) rotate(-1deg);
  }
  100% {
    transform: translate(0, 0) rotate(0deg);
  }
}

/* 修改原有的动画 */
@keyframes pulseBackground {
  0% {
    opacity: 0.3;
    transform: scale(1) rotate(0deg);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.05) rotate(2deg);
  }
  100% {
    opacity: 0.3;
    transform: scale(1) rotate(0deg);
  }
}

/* 确保消息容器不会溢出 */
.messages-container {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 100%;
}

/* 确保消息内容不会溢出 */
.message-content {
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

/* 优化代码块显示 */
.code-block {
  max-width: 100%;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

/* 其他样式保持不变... */
.ai-chat-container {
  width: 100%;
  /* max-width: 900px; */
  height: auto;
  max-height: 80vh;
  min-height: 500px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(245, 187, 68, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(245, 187, 68, 0.15);
  touch-action: auto;
}

/* 优化头部导航栏样式 */
.chat-header {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  padding: 20px 30px;
  border-bottom: 1px solid rgba(245, 187, 68, 0.1);
  position: relative;
  z-index: 10;
  transition: all 0.3s ease;
}

.chat-header:hover {
  background: rgba(255, 255, 255, 0.9);
}

/* 添加渐变装饰 */
.chat-header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(245, 187, 68, 0.3),
    transparent
  );
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
}

/* 优化 Logo 样式 */
.logo {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 8px 15px;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.logo-image {
  height: 35px;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(245, 187, 68, 0.1));
  transition: all 0.3s ease;
}

.logo:hover .logo-image {
  transform: scale(1.05);
  filter: drop-shadow(0 4px 8px rgba(245, 187, 68, 0.2));
}

/* 移除之前的 logo 相关样式 */
.logo i {
  display: none;
}

.logo span {
  display: none;
}

/* 优化操作按钮 */
.action-button {
  padding: 8px 16px;
  border: none;
  border-radius: 10px;
  background: rgba(245, 187, 68, 0.1);
  color: #f5bb44;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  backdrop-filter: blur(4px);
}

.action-button:hover {
  background: rgba(245, 187, 68, 0.15);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(245, 187, 68, 0.1);
}

.action-button i {
  font-size: 16px;
  transition: transform 0.3s ease;
}

.action-button:hover i {
  transform: rotate(10deg);
}

/* 添加新的动画效果 */
@keyframes floatIcon {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
}

/* 响应式调整 */
@media (max-width: 768px) {
  .chat-header {
    padding: 15px 20px;
  }

  .logo {
    padding: 6px 12px;
  }

  .logo i {
    font-size: 20px;
  }

  .logo span {
    font-size: 16px;
  }

  .action-button {
    padding: 8px;
  }

  .action-button span {
    display: none;
  }
}

/* 优化消息样式 */
.message-wrapper.user .message-content {
  background: linear-gradient(135deg, #f5bb44 0%, #f4a834 100%);
  color: white;
  border: none;
}

.message-wrapper:not(.user) .message-content {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(245, 187, 68, 0.1);
}

/* 优化滚动条 */
.chat-body::-webkit-scrollbar {
  width: 6px;
}

.chat-body::-webkit-scrollbar-track {
  background: rgba(245, 187, 68, 0.05);
  border-radius: 3px;
}

.chat-body::-webkit-scrollbar-thumb {
  background: rgba(245, 187, 68, 0.2);
  border-radius: 3px;
  transition: background 0.3s;
}

.chat-body::-webkit-scrollbar-thumb:hover {
  background: rgba(245, 187, 68, 0.3);
}

.message-wrapper {
  display: flex;
  gap: 16px;
  margin-bottom: 30px;
  animation: slideIn 0.3s ease;
  position: relative;
  z-index: 1;
}

.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5bb44, #f4a834);
  color: white;
  font-size: 18px;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(245, 187, 68, 0.2);
  position: relative;
  z-index: 1;
}

.message-wrapper.user .message-avatar {
  background: linear-gradient(135deg, #f4a834, #f5bb44);
}

.message-content {
  flex: 1;
  max-width: calc(100% - 56px);
  padding: 16px 20px;
  border-radius: 12px;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.sender {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.time {
  font-size: 12px;
  color: #95a5a6;
}

.message-text {
  line-height: 1.6;
  color: #34495e;
  font-size: 15px;
  white-space: pre-wrap;
  word-break: break-word;
}

.message-actions {
  opacity: 0;
  transition: opacity 0.3s;
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.message-content:hover .message-actions {
  opacity: 1;
}

.action-btn {
  padding: 6px 10px;
  border: none;
  background: white;
  border-radius: 6px;
  color: #666;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-btn:hover {
  background: #f5bb44;
  color: white;
}

.chat-input {
  padding: 20px 30px;
  background: #ffffff;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  position: relative;
  z-index: 10;
}

.input-wrapper {
  display: flex;
  gap: 16px;
  align-items: flex-end;
}

textarea {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  resize: none;
  font-size: 15px;
  line-height: 1.5;
  min-height: 24px;
  max-height: 150px;
  transition: all 0.3s;
  background: #f8f9fa;
}

textarea:focus {
  outline: none;
  border-color: #f5bb44;
  background: white;
  box-shadow: 0 2px 12px rgba(245, 187, 68, 0.1);
}

.send-btn {
  padding: 12px 24px;
  border: none;
  background: linear-gradient(135deg, #f5bb44, #f4a834);
  color: white;
  border-radius: 12px;
  cursor: pointer;
  font-size: 15px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.send-btn:not(:disabled):hover {
  box-shadow: 0 4px 12px rgba(245, 187, 68, 0.3);
}

.send-btn:disabled {
  background: #e0e0e0;
  cursor: not-allowed;
}

/* 欢迎界面样式优化 */
.welcome-screen {
  text-align: center;
  padding: 60px 20px;
}

.welcome-header {
  margin-bottom: 40px;
}

.welcome-header i {
  font-size: 48px;
  color: #f5bb44;
  margin-bottom: 20px;
}

.welcome-header h2 {
  font-size: 24px;
  color: #2c3e50;
  margin: 0;
}

.suggestion-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  /* max-width: 800px; */
  margin: 0 auto;
  position: relative;
  z-index: 2;
}

.suggestion-btn {
  padding: 16px 20px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #2c3e50;
  font-size: 15px;
  position: relative;
  z-index: 2;
}

.suggestion-btn:hover {
  border-color: #f5bb44;
  box-shadow: 0 4px 12px rgba(245, 187, 68, 0.1);
}

.suggestion-btn i {
  color: #f5bb44;
  font-size: 18px;
}

/* 添加新的加载动画样式 */
.loading-wrapper {
  padding: 8px 0;
  margin-top: 4px;
}

.loading-animation {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.loading-animation span {
  display: inline-block;
  width: 4px;
  height: 4px;
  background-color: #f5bb44;
  border-radius: 50%;
  opacity: 0.6;
  animation: loadingDots 1.4s infinite ease-in-out;
}

.loading-animation span:nth-child(1) {
  animation-delay: 0s;
}

.loading-animation span:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-animation span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes loadingDots {
  0%,
  100% {
    transform: scale(0.3);
    opacity: 0.3;
  }
  50% {
    transform: scale(1);
    opacity: 1;
    filter: brightness(1.1);
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
    filter: brightness(1.1);
  }
  100% {
    transform: scale(1);
  }
}

/* 消息动画优化 */
.message-enter-active,
.message-leave-active {
  transition: all 0.3s ease;
}

.message-enter-from,
.message-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.header-actions {
  display: flex;
  gap: 12px;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: rgba(245, 187, 68, 0.1);
  color: #f5bb44;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-button:hover {
  background: rgba(245, 187, 68, 0.2);
}

.action-button i {
  font-size: 16px;
}

.action-button span {
  display: inline-block;
}

/* 添加清空对话的确认弹窗样式 */
.confirm-dialog {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  width: 300px;
  text-align: center;
}

.confirm-dialog h3 {
  margin: 0 0 16px;
  color: #2c3e50;
}

.confirm-dialog .dialog-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
}

.confirm-dialog button {
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
}

.confirm-dialog .confirm-btn {
  background: #f5bb44;
  color: white;
}

.confirm-dialog .cancel-btn {
  background: #ecf0f1;
  color: #34495e;
}

.confirm-dialog button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 响应式调整 */
@media (max-width: 768px) {
  .action-button span {
    display: none;
  }

  .action-button {
    padding: 8px;
  }
}

/* 添加辅助色调 */
:root {
  --primary-color: #f5bb44;
  --primary-light: #fff9ef;
  --primary-dark: #f4a834;
  --text-primary: #2c3e50;
  --text-secondary: #666;
  --border-color: rgba(245, 187, 68, 0.15);
}

/* 优化消息内容样式 */
.message-content {
  border: 1px solid var(--border-color);
}

/* 优化光晕效果 */
.message-avatar::before {
  content: "";
  position: absolute;
  width: 100%;
  height: 100%;
  background: radial-gradient(
    circle at center,
    rgba(245, 187, 68, 0.2),
    transparent 70%
  );
  border-radius: 50%;
  animation: glow 2s infinite;
  z-index: -1;
}

@keyframes glow {
  0%,
  100% {
    opacity: 0.5;
    transform: scale(1.2);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.3);
  }
}

/* 代码块样式 */
:deep(.code-block) {
  background: #282c34;
  color: #abb2bf;
  padding: 16px;
  border-radius: 8px;
  margin: 12px 0;
  font-family: "Fira Code", monospace;
  font-size: 14px;
  line-height: 1.5;
  overflow-x: auto;
}

/* 行内代码样式 */
:deep(.inline-code) {
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: "Fira Code", monospace;
  font-size: 0.9em;
}

/* 表格样式 */
:deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

:deep(th),
:deep(td) {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

:deep(th) {
  background: #f5f7fa;
  font-weight: 600;
  color: #2c3e50;
}

:deep(tr:hover) {
  background: #f8fafc;
}

/* Markdown 样式 */
:deep(h1),
:deep(h2),
:deep(h3),
:deep(h4) {
  margin: 24px 0 16px;
  color: #2c3e50;
  font-weight: 600;
}

:deep(p) {
  margin: 16px 0;
  line-height: 1.6;
}

:deep(ul),
:deep(ol) {
  padding-left: 24px;
  margin: 16px 0;
}

:deep(li) {
  margin: 8px 0;
}

:deep(blockquote) {
  margin: 16px 0;
  padding: 0 16px;
  color: #666;
  border-left: 4px solid #ddd;
}

/* 图表容器样式 */
:deep(.chart-container) {
  margin: 20px 0;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

/* 链接样式 */
:deep(a) {
  color: #f5bb44;
  text-decoration: none;
  transition: all 0.3s ease;
}

:deep(a:hover) {
  color: #f4a834;
  text-decoration: underline;
}

/* 图片样式 */
:deep(img) {
  max-width: 100%;
  border-radius: 8px;
  margin: 16px 0;
}

/* 添加加粗和斜体文本的样式 */
:deep(strong) {
  font-weight: 600;
  color: #2c3e50;
}

:deep(em) {
  font-style: italic;
  color: #34495e;
}

/* 优化标题样式 */
:deep(h1),
:deep(h2),
:deep(h3),
:deep(h4) {
  margin: 24px 0 16px;
  color: #2c3e50;
  font-weight: 600;
  line-height: 1.4;
}

:deep(h1) {
  font-size: 2em;
  border-bottom: 2px solid rgba(245, 187, 68, 0.2);
  padding-bottom: 8px;
}

:deep(h2) {
  font-size: 1.5em;
  border-bottom: 1px solid rgba(245, 187, 68, 0.1);
  padding-bottom: 6px;
}

:deep(h3) {
  font-size: 1.25em;
}

:deep(h4) {
  font-size: 1.1em;
}
</style>
